import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';

import { activityService } from '../../../services/activityService';
import { shipmentService } from '../../../services/shipmentService';
import { BackButton } from '../../common/BackButton';
import { clearEditActivityDraft } from '../../../utils/activityDraftStorage';
import type { ActivityFormData } from './types';
import ActivityGroupEditor from './ActivityGroupEditor';
import type { GroupAudit } from './ActivityGroupSection';
import type { Contact } from '../../../types/contact';
import type { Product } from '../../../types/product';
import type { Stock } from '../../../types/stock';

// Simple unique ID generator
const generateId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export default function EditActivityGroupPage() {
    const { id, groupId } = useParams<{ id: string; groupId: string }>();
    const navigate = useNavigate();
    const { state } = useLocation();

    if (!id || !groupId) {
        throw new Error('Company ID and Group ID are required');
    }

    const companyId = parseInt(id);
    const groupIdNum = parseInt(groupId);
    const activitiesPath = `/companies/${companyId}/activities`;
    const returnPath = (state as { returnPath?: string } | null)?.returnPath ?? activitiesPath;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [initialDescription, setInitialDescription] = useState('');
    const [initialActivities, setInitialActivities] = useState<ActivityFormData[]>([]);
    const [initialPaidByClientAmount, setInitialPaidByClientAmount] = useState(0);
    const [initialPaidToProviderAmount, setInitialPaidToProviderAmount] = useState(0);
    const [initialAudit, setInitialAudit] = useState<GroupAudit | undefined>(undefined);
    const [hasActiveShipment, setHasActiveShipment] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchGroup = async () => {
            try {
                const [group, shipment] = await Promise.all([
                    activityService.getActivityGroupById(groupIdNum),
                    shipmentService.getByActivityGroupId(groupIdNum),
                ]);
                if (cancelled) return;
                setHasActiveShipment(shipment !== null);
                setInitialDescription(group.activityGroup.description || '');
                setInitialPaidByClientAmount(group.activityGroup.paidByClientAmount);
                setInitialPaidToProviderAmount(group.activityGroup.paidToProviderAmount);
                setInitialAudit({
                    createdDate: group.activityGroup.createdDate,
                    createdUserName: group.activityGroup.createdUserName,
                    updatedDate: group.activityGroup.updatedDate,
                    updatedUserName: group.activityGroup.updatedUserName,
                });
                setInitialActivities(
                    group.activities.map((activity) => ({
                        feId: generateId(),
                        bid: activity.id,
                        activityTypeId: activity.activityTypeId,
                        productId: activity.productId,
                        amount: activity.amount,
                        unitPrice: activity.unitPrice,
                        stockId: activity.stockId || undefined,
                        stockFromId: activity.stockFromId || undefined,
                        clientId: activity.clientId || undefined,
                        providerId: activity.providerId || undefined,
                        margin: activity.margin,
                        unitCostBasis: activity.unitCostBasis,
                        clientOrganizationName: activity.clientOrganizationName,
                        providerOrganizationName: activity.providerOrganizationName,
                        // Only id/name(-ish) fields come back from the group endpoint — enough for
                        // display (Chip label, formatContactFullName), not a full entity, hence
                        // the `as unknown as X` rather than satisfying the type structurally.
                        selectedProduct: {
                            id: activity.productId,
                            name: activity.productName,
                            mainImageId: activity.productMainImageId,
                        } as unknown as Product,
                        selectedStock: activity.stockId
                            ? { id: activity.stockId, name: activity.stockName } as unknown as Stock
                            : undefined,
                        selectedStockFrom: activity.stockFromId
                            ? { id: activity.stockFromId, name: activity.stockFromName } as unknown as Stock
                            : undefined,
                        selectedClient: activity.clientId ? {
                            id: activity.clientId,
                            lastName: activity.clientLastName,
                            firstName: activity.clientFirstName,
                            middleName: activity.clientMiddleName,
                        } as unknown as Contact : undefined,
                        selectedProvider: activity.providerId ? {
                            id: activity.providerId,
                            lastName: activity.providerLastName,
                            firstName: activity.providerFirstName,
                            middleName: activity.providerMiddleName,
                        } as unknown as Contact : undefined,
                    }))
                );
            } catch (err) {
                console.error('Failed to load activity group:', err);
                if (!cancelled) {
                    setError('Не вдалося завантажити групу активностей');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        fetchGroup();
        return () => {
            cancelled = true;
        };
    }, [groupIdNum, companyId]);

    if (loading) {
        return (
            <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
                <BackButton
                    label="Назад"
                    onClick={() => {
                        clearEditActivityDraft(companyId, groupIdNum);
                        navigate(returnPath);
                    }}
                />
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <BackButton
                label="Назад"
                onClick={() => {
                    clearEditActivityDraft(companyId, groupIdNum);
                    navigate(returnPath);
                }}
            />

            <Typography variant="h4" component="h1" gutterBottom>
                Редагувати групу активностей
            </Typography>

            {hasActiveShipment && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Для цієї групи вже створено ТТН у Новій пошті. Зміна суми чи кількості тут{' '}
                    <strong>не оновлює</strong> вагу чи оголошену вартість у самій відправці —
                    відредагуйте їх вручну в кабінеті Нової пошти, якщо це потрібно.
                </Alert>
            )}

            <ActivityGroupEditor
                companyId={companyId}
                groupBid={groupIdNum}
                initialDescription={initialDescription}
                initialActivities={initialActivities}
                initialPaidByClientAmount={initialPaidByClientAmount}
                initialPaidToProviderAmount={initialPaidToProviderAmount}
                audit={initialAudit}
                onSuccess={() => navigate(returnPath)}
                onCancel={() => {
                    clearEditActivityDraft(companyId, groupIdNum);
                    navigate(returnPath);
                }}
            />
        </Box>
    );
}
